import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { getAuthToken } from "@/lib/api";
import { useMe } from "@/features/auth/useMe";
import { CoachTabStrip, type CoachTab } from "./CoachTabStrip";
import { RosterPage } from "./RosterPage";
import { NotesPage } from "./NotesPage";
import { ClipsPage } from "./ClipsPage";
import { PlaylistsPage } from "./PlaylistsPage";
import { MatchSummariesPage } from "./MatchSummariesPage";
import { EngagementPage } from "./EngagementPage";
import { SettingsPage } from "./SettingsPage";
import { CoachMatchesPage } from "./CoachMatchesPage";
import {
  GoalComposerModal,
  type GoalComposerSource,
} from "./components/GoalComposerModal";
import { CoachDevelopmentModal } from "./components/CoachDevelopmentModal";
import { useCoachGoalMutations } from "./hooks/useCoachGoals";
import { TeamScopeBar } from "@/features/navigation/TeamScopeBar";
import { TeamScopeGate } from "@/features/navigation/TeamScopeGate";
import { getPublicNavCapabilities } from "@/features/navigation/navCapabilities";
import { useScopeStore } from "@/stores/scope";
import type { PlayerGoal } from "@/lib/types";

/**
 * Coach workspace tabbed shell — the host for the `/coach` list
 * pages (Roster · Notes · Playlists · Clips · Match Summaries ·
 * Engagement · Settings). Ports the legacy `setCoachTab` /
 * `renderCoachWorkspace` shell from `js/coaching.js`.
 *
 * Routing: `/coach?tab=<tab>`. The `tab` search param selects the
 * list page; absent ⇒ Roster (legacy default — legacy drops the
 * `?tab=` param entirely for roster). `/coach/review` is a
 * SEPARATE route (the M4/M5 surface) — the "Review" tab in the
 * strip links there rather than rendering under `?tab=review`, so
 * Coach Review's own `match_id`/`note_id`/`clip_id` schema and the
 * 40 passing M4/M5 E2E tests are untouched.
 *
 * `useMe()` reconciles the auth + scope stores on a direct nav —
 * the list-page queries (Roster/Notes/etc.) are scope-gated, same
 * as Coach Review needs it (the M5 latent-bug fix).
 *
 * M6a slice 1 ships the shell + routing + tab strip. The list
 * pages themselves are placeholders here and get built in the
 * following M6a slices — each one swaps its placeholder for the
 * real surface.
 */

/** The tabs the SHELL hosts — Review is its own route, so it's not
 * in this set even though it's in the strip. */
type ShellTab = Exclude<CoachTab, "review">;

const SHELL_TAB_LABELS: Record<ShellTab, string> = {
  roster: "Roster",
  matches: "Matches",
  notes: "Notes",
  playlists: "Playlists",
  clips: "Clips",
  summaries: "Match Summaries",
  engagement: "Engagement",
  settings: "Settings",
};

export function CoachShell() {
  // Reconcile auth + scope stores — the list-page queries are
  // scope-gated, and a direct nav to /coach must not leave them
  // unreconciled (the M5 CoachReview latent-bug pattern).
  const me = useMe();
  const navigate = useNavigate({ from: "/coach" });

  // Auth gate. Mirrors FeedbackShell: no stored token OR an explicit
  // 401 from /api/me ⇒ redirect to `/login?next=<current path>`. A
  // `useRef` flag prevents the effect from re-firing after the
  // redirect URL change races with the in-flight /api/me resolution.
  //
  // `next` captures the FULL path + search (`/coach?tab=clips`) at
  // mount via a ref so a viewer deep-linked to a specific tab lands
  // back on that tab after signing in, not the roster default. The
  // ref freezes the value before the redirect rewrites the URL.
  // `window.location` is the same source `PublicAppHeader` uses to
  // build its `loginNext`.
  const noToken = !getAuthToken();
  const isAnon = me.data === undefined ? false : me.data?.user == null;
  const showLogin = noToken || isAnon;
  const authGateMountPath = useRef(
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}` || "/coach"
      : "/coach",
  );
  const authRedirectFired = useRef(false);
  useEffect(() => {
    if (!showLogin) return;
    if (authRedirectFired.current) return;
    authRedirectFired.current = true;
    void navigate({
      to: "/login",
      search: { next: authGateMountPath.current },
      replace: true,
    });
  }, [showLogin, navigate]);

  // Wrong-role gate. A signed-in user with no coach access (a plain
  // player / guardian, an uploader-only admin) who reaches /coach via
  // a stale bookmark or a hand-typed URL gets bounced to `/hub`, the
  // role-based redirect, so they land on their OWN home instead of a
  // shell whose scope-gated queries would just 403. This mirrors the
  // exact `canOpenCoach` rule `PublicAppHeader` uses to show/hide the
  // Coach link — the gate and the nav stay in lock-step. Anonymous
  // users are handled by the auth gate above (this effect waits for
  // `me.data?.user` to resolve), so the two never fight.
  const canOpenCoach = getPublicNavCapabilities(me.data).canOpenCoach;
  const roleRedirectFired = useRef(false);
  useEffect(() => {
    if (showLogin) return;
    if (!me.data?.user) return;
    if (canOpenCoach) return;
    if (roleRedirectFired.current) return;
    roleRedirectFired.current = true;
    void navigate({ to: "/hub", replace: true });
  }, [showLogin, me.data, canOpenCoach, navigate]);

  const search = useSearch({ from: "/coach" });
  // `tab` absent ⇒ roster (legacy default).
  const activeTab: ShellTab = search.tab ?? "roster";

  // Multi-team users who haven't picked a team yet must do so before
  // any scope-gated tab renders — otherwise the coach surfaces query
  // against a stale/empty scope.
  const selectionRequired = useScopeStore((s) => s.selectionRequired);

  // The goal-composer modal + the Player Development modal are
  // both owned by the SHELL, not a list page — holding their
  // state here keeps "one modal at a time" structural (the
  // `GoalComposerModal` and `CoachDevelopmentModal` both wrap
  // `CoachModal`, so two open at once would both grab Escape).
  //
  // `goalModal`:
  //   - `null`                       → closed
  //   - `{ mode: "create", playerId}`→ create a goal for a player
  //   - `{ mode: "edit", goal }`     → edit an existing goal
  // `devModalPlayer`: the player whose Development modal is open.
  // Opening the goal composer from the dev modal CLOSES the dev
  // modal first (legacy nests them; we don't, to keep Escape sane).
  const goalMutations = useCoachGoalMutations();
  const [goalModal, setGoalModal] = useState<
    | {
        mode: "create";
        playerId: string;
        /** M7.5 D4: optional source evidence — set when the modal
         * opens from a Notes / Clips list row's "+ New goal"
         * button. Plumbs `source_note_id` / `source_clip_id` +
         * seed title/description through `<GoalComposerModal>`. */
        source?: GoalComposerSource;
      }
    | { mode: "edit"; goal: PlayerGoal }
    | null
  >(null);
  const [devModalPlayer, setDevModalPlayer] = useState<{
    id: string;
    name: string;
  } | null>(null);

  if (showLogin) {
    return (
      <main
        className="min-h-screen p-6 text-ink"
        data-testid="coach-shell-auth-redirect"
      />
    );
  }

  // Signed-in but no coach access — the wrong-role effect above is
  // mid-redirect to `/hub`. Render an empty shell so the scope-gated
  // coach tabs never mount (and never fire queries that would 403)
  // for the frame before the navigate lands.
  if (me.data?.user && !canOpenCoach) {
    return (
      <main
        className="min-h-screen p-6 text-ink"
        data-testid="coach-shell-role-redirect"
      />
    );
  }

  return (
    <main className="min-h-screen p-4 text-ink sm:p-6">
      {/* Constant workspace-level header — legacy `coach-page-head`
       * (`index.html`). The TAB name is NOT repeated here; it's the
       * per-page card header below that names + describes each tab.
       * `<CoachReview>` carries its own header (it's a separate
       * route), so this one is the shell's alone. */}
      <header className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row">
        <div className="min-w-0">
          <h1 className="text-2xl font-display tracking-tight sm:text-3xl">
            Coaching Workspace
          </h1>
          <p className="text-ink-muted text-sm mt-1">
            Manage your roster, write timestamped notes, build review
            playlists, and mark up video — all in one place.
          </p>
        </div>
        <div className="w-full sm:w-auto">
          <TeamScopeBar />
        </div>
      </header>

      {/* The sub-tab strip leads the tab content. `<CoachReview>`
       * renders the SAME `mb-4` strip wrapper so the two routes'
       * chrome lines up — see its render(). */}
      <div className="mb-4">
        <CoachTabStrip active={activeTab} />
      </div>

      {selectionRequired ? (
        <TeamScopeGate />
      ) : (
      <section
        aria-label={SHELL_TAB_LABELS[activeTab]}
        data-testid={`coach-shell-panel-${activeTab}`}
      >
        {/* Every coach tab is now a real surface — the placeholder
         * fallback only fires for an unreachable tab value. */}
        {activeTab === "roster" ? (
          <RosterPage
            onCreatePlayerGoal={(playerId) =>
              setGoalModal({ mode: "create", playerId })
            }
            onOpenDevelopment={(id, name) =>
              setDevModalPlayer({ id, name })
            }
          />
        ) : activeTab === "notes" ? (
          // M7.5 D1: "+ New goal" on Notes rows opens the goal
          // composer with the note as source evidence.
          <NotesPage
            onCreatePlayerGoal={(playerId, source) =>
              setGoalModal({ mode: "create", playerId, source })
            }
          />
        ) : activeTab === "clips" ? (
          // M7.5 D2: same for Clips rows.
          <ClipsPage
            onCreatePlayerGoal={(playerId, source) =>
              setGoalModal({ mode: "create", playerId, source })
            }
          />
        ) : activeTab === "playlists" ? (
          <PlaylistsPage />
        ) : activeTab === "summaries" ? (
          <MatchSummariesPage />
        ) : activeTab === "engagement" ? (
          <EngagementPage />
        ) : activeTab === "matches" ? (
          <CoachMatchesPage />
        ) : activeTab === "settings" ? (
          <SettingsPage />
        ) : (
          <CoachTabPlaceholder tab={activeTab} />
        )}
      </section>
      )}

      {/* Player Development modal — opened from a Roster row's
       * "Development profile" button. Its goal-composer entry
       * points close THIS modal and open the composer (one modal
       * at a time). */}
      {devModalPlayer ? (
        <CoachDevelopmentModal
          playerId={devModalPlayer.id}
          playerName={devModalPlayer.name}
          onClose={() => setDevModalPlayer(null)}
          onNewGoal={(playerId) => {
            setDevModalPlayer(null);
            setGoalModal({ mode: "create", playerId });
          }}
          onEditGoal={(goal) => {
            setDevModalPlayer(null);
            setGoalModal({ mode: "edit", goal });
          }}
        />
      ) : null}

      {/* Goal-composer modal — create (from a Roster row or the
       * dev modal) or edit (from the dev modal's goal cards). */}
      {goalModal ? (
        <GoalComposerModal
          goal={goalModal.mode === "edit" ? goalModal.goal : null}
          preselectedPlayerId={
            goalModal.mode === "create" ? goalModal.playerId : undefined
          }
          source={
            goalModal.mode === "create" ? goalModal.source : undefined
          }
          mutations={goalMutations}
          onClose={() => setGoalModal(null)}
        />
      ) : null}
    </main>
  );
}

/** Temporary per-tab placeholder — replaced surface-by-surface in
 * the M6a slices that follow the shell. */
function CoachTabPlaceholder({ tab }: { tab: ShellTab }) {
  return (
    <div
      className="rounded-lg border border-rule bg-surface/40 px-6 py-12 text-center"
      data-testid="coach-shell-placeholder"
    >
      <p className="text-ink font-display text-lg">
        {SHELL_TAB_LABELS[tab]}
      </p>
      <p className="text-ink-muted text-sm mt-1">
        This surface lands in an upcoming M6a slice.
      </p>
    </div>
  );
}
